import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Skeleton } from 'primereact/skeleton';
import { Button } from '../../../../../components/ui/Button';

import {
  useGetCampaignLayerContent,
  useGetPlaylistLayerContent,
  useGetPlaylist,
  useDeleteCampaignContent,
  useUpdateCampaignContentSettings,
  useUpdateCampaignLayerSettings,
} from '../../../../../queries';
import { useDeletePlaylistContent } from '../../../../../queries/playlistContent';
// Not in the queries/index.js barrel — reused directly from where the
// existing "publish blast-radius" disclosure (the playlist gallery's own
// publish button) already lives.
import { usePlaylistPublishImpact } from '../../../../../queries/showPage';
import { setLayer, setLayerSettingsTab, setAddContentSource, setDialog, toggleDialog } from '../../../../../redux/Slices/Global/globalSlice';
import { readImageFit } from '../../../../../utils/imageFit';
import { useCustomToast } from '../../../../Contexts/ToastContext';
import MediaThumb from '../../../../SharedComponents/DragDrop/components/MediaThumb';
import BuilderDuration from '../../../../SharedComponents/DragDrop/components/BuilderDuration';
import BuilderItemSettingsSheet from '../../../../SharedComponents/DragDrop/BuilderItemSettingsSheet';
import PlaylistAccordionRow from '../../../../SharedComponents/DragDrop/PlaylistAccordionRow';
import { usePlaylistFoldState } from '../../../../SharedComponents/DragDrop/usePlaylistFoldState';
import { useMediaPreview, isPreviewable } from '../../../../SharedComponents/MediaPreview/MediaPreviewContext';

const AUTO_TAG_NAME = 'Auto-created';

/**
 * "الصور (أجواء)" — row-list rewrite (pixel-matched from
 * .claude/pp-campaign-builder-prototype.html's .item/.thumb/.it-txt/
 * .it-actions, reusing the SAME pp-cb-item* classes CampaignCard's own
 * builderRow branch already uses for MAIN rows).
 *
 * PLAYGROUND can hold MULTIPLE rows (assignCampaignPlayLists only ever
 * APPENDS — see B2 orchestration in MediaList.jsx) — every row is rendered,
 * not just one found via .find(). A row is either:
 *   - a raw media row (row.media_id set) — legacy/back-compat shape, one
 *     item, rendered directly.
 *   - a playlist row (row.playlist_id set) — delegated to VibesPlaylistRow
 *     so its OWN hooks (useGetPlaylist/usePlaylistPublishImpact/
 *     useGetPlaylistLayerContent) can be called per-row without breaking
 *     the rules of hooks.
 */
function CampaignVibesSection() {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { openDialog, campaignDetails } = useSelector((state) => state.global);
  const { showToast } = useCustomToast();
  // F3: the builder's own item-settings sheet (new, local state — not the
  // old openDialog.mediaSettingPopup/playlistSettingPopup + selectedMedia
  // pair MediaSettingPopup/PlaylistSettingPopup used, which this section no
  // longer renders at all).
  const [builderItemSheet, setBuilderItemSheet] = useState(null);

  const { isLoading, data, isError } = useGetCampaignLayerContent(id, 'PLAYGROUND');
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const deleteContent = useDeleteCampaignContent();
  const updateLayerSettings = useUpdateCampaignLayerSettings();

  // Item 1 fix: the campaign's own auto-playlist (Fast-Create's "Auto-
  // created" tag, campaign_count<=1) renders as FLAT rows, not an accordion
  // (see VibesPlaylistRow below) — so it never folds, and the VibesDisplayBar
  // (عرض الصور + الانتقال) belongs at SECTION level, above the whole rows
  // list, exactly like .claude/pp-campaign-builder-prototype.html's `#vbar`
  // div sitting above `#vibesArea` — not per-row. VibesPlaylistRow can only
  // tell us it's the own-auto-playlist via its own hooks (tags + publish-
  // impact), so it reports that state up here via `onOwnAutoState`.
  const [ownAutoInfo, setOwnAutoInfo] = useState(null); // { row, playlistId, hasItems } | null
  const handleOwnAutoState = useCallback((info) => setOwnAutoInfo(info), []);

  // Spec item D: fold-all button + per-viewer persisted fold state, only
  // relevant when this layer has at least one playlist row that's actually
  // rendered as an accordion (excludes the own-auto-playlist, which never
  // is one — see above).
  const vibesPlaylistIds = useMemo(
    () =>
      rows
        .filter((row) => row.playlist_id != null && row.playlist_id !== ownAutoInfo?.playlistId)
        .map((row) => row.playlist_id),
    [rows, ownAutoInfo?.playlistId]
  );
  const vibesFold = usePlaylistFoldState(id, 'PLAYGROUND', vibesPlaylistIds);

  // F2: unified-settings visibility for الصور (أجواء) — same PLAYGROUND
  // layer policy MAIN's own banner reads, just scoped here.
  const vibesLayerPolicy = campaignDetails?.settings?.live?.layer_settings?.PLAYGROUND || {};
  const vibesUnifyActive = !!vibesLayerPolicy.apply_layer_settings;

  const invalidatePlayground = () => {
    queryClient.invalidateQueries(['getCampaignLayerContent', id, 'PLAYGROUND']);
  };

  const openPicker = () => {
    dispatch(setLayer('PLAYGROUND'));
    dispatch(setAddContentSource('vibes'));
    dispatch(toggleDialog('addContent'));
  };

  const openLayerSheet = (tab = 'display') => {
    dispatch(setLayer('PLAYGROUND'));
    dispatch(setLayerSettingsTab(tab));
    dispatch(toggleDialog('layerSettings'));
  };
  // See CampaignContentSetup.jsx's own disableMainUnify for why the dialog
  // flag has to be forced closed afterward — useUpdateCampaignLayerSettings's
  // onSuccess unconditionally TOGGLES it, meant for the sheet's own save
  // button, which would pop the sheet open here since it starts closed.
  const disableVibesUnify = async () => {
    await updateLayerSettings.mutateAsync({ id, layer: 'PLAYGROUND', layer_settings: { apply_layer_settings: false } });
    dispatch(setDialog({ key: 'layerSettings', value: false }));
  };

  const removeMediaRow = async (row) => {
    await deleteContent.mutateAsync({ id: row.campaign_playlist_media_id, campaign_id: id });
    invalidatePlayground();
    // Spec item F: whole-playlist-row removal (detach from this layer only)
    // gets the exact required toast wording. Plain media-row deletes keep
    // their existing silent behavior.
    if (row.playlist_id) {
      showToast('success', t('show.show_vibes_title'), t('dragdrop.playlist_removed_from_layer_toast', { name: row.playlist_title }));
    }
  };

  return (
    <section className="pp-cb-section">
      <div className="pp-cb-sec-head">
        <div className="pp-cb-txt">
          <h2>
            {t('show.show_vibes_title')}
            {vibesUnifyActive && <span className="tag">{t('show.show_unified_badge')}</span>}
          </h2>
          <div className="pp-cb-desc">{t('show.show_vibes_desc')}</div>
        </div>
        {vibesPlaylistIds.length > 0 && (
          <button
            type="button"
            className="pp-cb-foldall"
            onClick={() => vibesFold.setAll(!vibesFold.anyOpen)}
            title={vibesFold.anyOpen ? t('dragdrop.fold_all_playlists') : t('dragdrop.unfold_all_playlists')}
          >
            <i className={`pi ${vibesFold.anyOpen ? 'pi-angle-double-up' : 'pi-angle-double-down'}`} />
            <span className="fl">{vibesFold.anyOpen ? t('dragdrop.fold_all_playlists') : t('dragdrop.unfold_all_playlists')}</span>
          </button>
        )}
        <button className="pp-cb-icbtn" title={t('show.show_layer_settings')} aria-label={t('show.show_layer_settings')} onClick={() => openLayerSheet('display')}>
          <i className="pi pi-cog" />
        </button>
      </div>

      {vibesUnifyActive && (
        <div className="pp-cb-warn uni">
          <div className="wt">
            <i className="pi pi-exclamation-triangle" />
            <span>{t('show.show_unify_banner_text')}</span>
          </div>
          <div className="wa">
            <Button outlined label={t('show.show_unify_disable')} onClick={disableVibesUnify} loading={updateLayerSettings.isLoading} />
            <Button text label={t('show.show_unify_open')} onClick={() => openLayerSheet('display')} />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="pp-cb-sk">
          <Skeleton shape="square" size="46px" borderRadius="11px" />
          <div style={{ flex: 1 }}>
            <Skeleton width="55%" height="13px" />
            <Skeleton width="32%" height="11px" style={{ marginTop: 8 }} />
          </div>
        </div>
      ) : isError ? (
        <div className="pp-cb-additem" style={{ cursor: 'default', color: 'var(--pp-btn-coral)' }}>
          {t('show.show_main_content_error_title')}
        </div>
      ) : rows.length === 0 ? (
        <button className="pp-cb-additem" onClick={openPicker}>
          <i className="pi pi-plus" />
          {t('show.show_vibes_empty_add')}
        </button>
      ) : (
        <>
          {ownAutoInfo?.hasItems && (
            <VibesDisplayBar
              row={ownAutoInfo.row}
              playlistId={ownAutoInfo.playlistId}
              onRemovePlaylist={() => removeMediaRow(ownAutoInfo.row)}
            />
          )}
          <div className="pp-cb-list">
            {rows.map((row) =>
              row.playlist_id ? (
                <VibesPlaylistRow
                  key={row.campaign_playlist_media_id ?? row.playlist_id}
                  row={row}
                  campaignId={id}
                  open={vibesFold.isOpen(row.playlist_id)}
                  onToggleOpen={() => vibesFold.toggle(row.playlist_id)}
                  onOpenSettings={(r) => setBuilderItemSheet({ item: r, isPlaylist: true })}
                  onRemoveRow={() => removeMediaRow(row)}
                  onOwnAutoState={handleOwnAutoState}
                />
              ) : (
                <VibesMediaRow
                  key={row.campaign_playlist_media_id ?? row.media_id}
                  row={row}
                  onOpenSettings={(r) => setBuilderItemSheet({ item: r, isPlaylist: false })}
                  onRemove={() => removeMediaRow(row)}
                  isDeleting={deleteContent.isLoading}
                />
              )
            )}
          </div>
          <button className="pp-cb-additem" onClick={openPicker}>
            <i className="pi pi-plus" />
            {t('addcontent.vibes_add_more')}
          </button>
        </>
      )}

      {builderItemSheet && (
        <BuilderItemSettingsSheet
          item={builderItemSheet.item}
          layer="PLAYGROUND"
          isPlaylist={builderItemSheet.isPlaylist}
          unifyActive={vibesUnifyActive}
          onClose={() => setBuilderItemSheet(null)}
        />
      )}
      {/* The layer sheet itself is rendered ONCE, by the parent
          (CampaignContentSetup.jsx) — it reads the shared `layer` redux
          value, so rendering a second instance here (gated on the same
          openDialog.layerSettings flag) would stack two identical sheets
          whenever either section's gear opens it. */}
    </section>
  );
}

/** A raw media row directly on PLAYGROUND (row.media_id set) — legacy/
 * back-compat shape; B2's own orchestration never writes this shape, it
 * only ever produces playlist rows, but existing campaigns may already
 * have one from before this build. */
function VibesMediaRow({ row, onOpenSettings, onRemove, isDeleting }) {
  const { t } = useTranslation();
  const mediaPreview = useMediaPreview();
  // A legacy/back-compat raw media row directly on the layer — no sibling
  // list of its own (unlike MAIN's `data` or a playlist's `items`), so its
  // navigation scope is just itself (nav arrows naturally disabled at n=1).
  const openPreview = () => mediaPreview.openLightbox([row], 0, { stopAudioOnClose: true });
  const up = String(row.type || '').toUpperCase();
  const isVideo = up === 'VIDEO';
  const typeLabel = isVideo ? t('show.show_type_video') : up === 'IMAGE' ? t('show.show_type_image') : t('show.show_type_other');
  // No "· مكتوم الصوت" suffix for images per prototype — video gets it (it
  // carries real audio, muted by this layer's own always-muted contract);
  // a still image has no audio to disclose.
  const meta = isVideo ? `${typeLabel} · ${t('show.show_vibes_muted_suffix')}` : typeLabel;

  return (
    <div className="pp-cb-item">
      <div className="pp-cb-item-thumb"><MediaThumb item={row} onPreview={openPreview} /></div>
      <div className="pp-cb-item-txt">
        <span className="n">{row.file_name || row.title}</span>
        <span className="m">{meta}</span>
      </div>
      <div className="pp-cb-item-actions">
        <button className="pp-cb-chipbtn" title={t('dragdrop.setting')} onClick={() => onOpenSettings(row)}>
          <i className="pi pi-cog" />
        </button>
        <button className="pp-cb-chipbtn del" title={t('buttons.delete')} onClick={onRemove} disabled={isDeleting}>
          <i className="bx bx-trash" />
        </button>
      </div>
    </div>
  );
}

/** A playlist row on PLAYGROUND (row.playlist_id set).
 *
 * Item 1 fix: this is NOT one uniform behavior any more. `isOwnAutoPlaylist`
 * (Fast-Create's own "Auto-created"-tagged playlist, campaign_count<=1)
 * renders as FLAT individual rows (thumb/name/editable duration/delete)
 * directly in the VIBES list — matching .claude/pp-campaign-builder-
 * prototype.html's VIBES demo data, where the campaign's own images/video
 * are plain top-level VIBES entries, never wrapped in a `.pl` accordion;
 * only genuinely shared/picked playlists (shown in that same prototype as
 * "صور الصباح"/"ديكور المقهى") use the `.pl` accordion. `onOwnAutoState` lets
 * this row report its own-auto items up to the SECTION, which renders the
 * VibesDisplayBar once, above the whole list (prototype's `#vbar` sits above
 * `#vibesArea`, not per-row) — own hooks per row are still required so
 * useGetPlaylist/usePlaylistPublishImpact/useGetPlaylistLayerContent aren't
 * called conditionally in the parent (rules of hooks).
 *
 * Judgment call on auto-detach-on-empty (`0ed910e6` deliberately removed it
 * for the shared-playlist case): NOT restored here either, even though it
 * would technically be safe for the own-auto-playlist specifically
 * (campaign_count<=1 by definition, so no other campaign could be affected).
 * Kept consistent with the shared-playlist empty state instead — one mental
 * model ("القائمة فارغة." + a manual trash to detach) for any playlist
 * reaching zero items, own or shared, rather than one playlist flavor
 * silently vanishing on its own and the other requiring a manual step.
 */
function VibesPlaylistRow({ row, campaignId, open, onToggleOpen, onOpenSettings, onRemoveRow, onOwnAutoState }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useCustomToast();

  const { playlistInfo } = useGetPlaylist(row.playlist_id);
  const { data: impact } = usePlaylistPublishImpact(row.playlist_id);
  const campaignCount = impact?.campaign_count ?? 1;
  const tagsList = Array.isArray(playlistInfo?.tags_list) ? playlistInfo.tags_list : [];
  const isOwnAutoPlaylist = tagsList.some((tg) => tg.name === AUTO_TAG_NAME) && campaignCount <= 1;

  const { isLoading, data: subItems, isError, refetch } = useGetPlaylistLayerContent(
    isOwnAutoPlaylist ? row.playlist_id : null,
    'PLAYGROUND'
  );
  const items = Array.isArray(subItems) ? subItems : [];
  const deletePlaylistContent = useDeletePlaylistContent();

  // v15 media preview — this own-auto playlist's flat items are their own
  // navigation scope, same rule as PlaylistAccordionRow.jsx's sub-items
  // (VIBES includes no audio here by construction — the sheet already
  // excludes audio from the vibes picker — but filtering through
  // isPreviewable costs nothing and keeps this consistent with every other
  // reuse point).
  const mediaPreview = useMediaPreview();
  const openSubPreview = (sub) => {
    const visualItems = items.filter(isPreviewable);
    const idx = visualItems.indexOf(sub);
    mediaPreview.openLightbox(visualItems, idx < 0 ? 0 : idx, { stopAudioOnClose: true });
  };

  // Spec item G (same rule PlaylistAccordionRow.jsx uses): any sub-item
  // edit/removal flips the campaign's own publish-dirty banner immediately.
  const afterEdit = () => {
    queryClient.invalidateQueries(['getCampaign', campaignId]);
  };

  const removeSubItem = async (sub) => {
    await deletePlaylistContent.mutateAsync({
      playlist_id: row.playlist_id,
      playlist_media_id: sub.playlist_media_id,
    });
    await refetch();
    afterEdit();
    showToast('success', t('show.show_vibes_title'), t('dragdrop.playlist_item_removed_toast', { name: sub.file_name || sub.title }));
  };

  useEffect(() => {
    if (!isOwnAutoPlaylist) return undefined;
    onOwnAutoState({ row, playlistId: row.playlist_id, hasItems: items.length > 0 });
    return () => onOwnAutoState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnAutoPlaylist, row, items.length, onOwnAutoState]);

  if (isOwnAutoPlaylist) {
    if (isLoading) {
      return (
        <div className="pp-cb-sk">
          <Skeleton shape="square" size="34px" borderRadius="8px" />
          <div style={{ flex: 1 }}>
            <Skeleton width="55%" height="11px" />
          </div>
        </div>
      );
    }
    if (isError) {
      return (
        <div className="pp-cb-pl-note err">
          <span>{t('dragdrop.playlist_items_load_error')}</span>
          <button type="button" className="pp-cb-chipbtn" title={t('buttons.retry')} onClick={() => refetch()}>
            <i className="pi pi-refresh" />
          </button>
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="pp-cb-item">
          <div className="pp-cb-pl-note" style={{ color: 'var(--pp-comp-muted)', flex: 1 }}>
            {t('dragdrop.playlist_empty')}
          </div>
          <div className="pp-cb-item-actions">
            <button className="pp-cb-chipbtn del" title={t('show.show_vibes_remove_playlist')} onClick={onRemoveRow}>
              <i className="bx bx-trash" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <>
        {items.map((sub) => {
          const isVideo = String(sub.type || '').toUpperCase() === 'VIDEO';
          const typeLabel = isVideo ? t('show.show_type_video') : t('show.show_type_image');
          const metaBase = isVideo ? `${typeLabel} · ${t('show.show_vibes_muted_suffix')}` : typeLabel;
          // F6: tap-to-edit for IMAGE rows only — a video's duration is its
          // own natural length, not overridable (matches the prototype's own
          // `editable = it.d && (list==='main' || it.t==='image')` gate).
          return (
            <div className="pp-cb-item" key={sub.playlist_media_id ?? sub.id}>
              <div className="pp-cb-item-thumb"><MediaThumb item={sub} onPreview={openSubPreview} /></div>
              <div className="pp-cb-item-txt">
                <span className="n">{sub.file_name || sub.title}</span>
                <span className="m">
                  {metaBase} · <BuilderDuration
                    seconds={sub.custom_duration}
                    editable={!isVideo}
                    source="playlist"
                    id={sub.playlist_media_id}
                    playlistId={row.playlist_id}
                    playgroundLayer="PLAYGROUND"
                    onSaved={afterEdit}
                  />
                </span>
              </div>
              <div className="pp-cb-item-actions">
                <button className="pp-cb-chipbtn del" title={t('buttons.delete')} onClick={() => removeSubItem(sub)}>
                  <i className="bx bx-trash" />
                </button>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <PlaylistAccordionRow
      row={row}
      layer="PLAYGROUND"
      campaignId={campaignId}
      open={open}
      onToggleOpen={onToggleOpen}
      thumbNode={<i className="fas fa-list-ul" style={{ color: 'var(--pp-btn-honey)' }} />}
      metaKind="image"
      showMutedSuffix
      toastTitle={t('show.show_vibes_title')}
      headerActions={
        <>
          <button className="pp-cb-chipbtn" title={t('dragdrop.setting')} onClick={() => onOpenSettings(row)}>
            <i className="pi pi-cog" />
          </button>
          <button className="pp-cb-chipbtn del" title={t('buttons.delete')} onClick={onRemoveRow}>
            <i className="bx bx-trash" />
          </button>
        </>
      }
    />
  );
}

const VBAR_FITS = ['fit', 'crop', 'fill'];

/** F5 — "عرض الصور" bar: writes `image_fit` onto the auto-playlist's own
 * PIVOT row (campaign_playlist_media), via the EXISTING per-item settings
 * endpoint (POST /campaign/media/{pivotId}/setting, merged into `settings`
 * server-side — same merge pattern the backend already uses, see
 * CampaignPlaylistController::campaignMediaSetting()). The transition chip
 * reads/opens the CURRENT PLAYGROUND layer's animation settings. */
function VibesDisplayBar({ row, playlistId, onRemovePlaylist }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { campaignDetails } = useSelector((s) => s.global);
  const updateItemSetting = useUpdateCampaignContentSettings();

  const [fit, setFit] = useState(() => readImageFit(row) || 'crop');
  useEffect(() => setFit(readImageFit(row) || 'crop'), [row]);

  const anim = campaignDetails?.settings?.live?.playground_media_animation;
  const transLabel = anim?.enabled
    ? `${t(`show.show_anim_type_${anim.type}`)} · ${Number(anim.duration).toFixed(1)}${t('show.show_seconds_unit')}`
    : t('show.show_trans_none');

  const setFitMode = async (mode) => {
    setFit(mode);
    const formData = new FormData();
    formData.append('image_fit', mode);
    await updateItemSetting.mutateAsync(
      { data: formData, id: row.campaign_playlist_media_id, suppressDialog: true },
      { onSuccess: () => playlistId && queryClient.invalidateQueries(['getPlaylistLayerContent', playlistId, 'PLAYGROUND']) }
    );
  };

  const openTransitionTab = () => {
    dispatch(setLayer('PLAYGROUND'));
    dispatch(setLayerSettingsTab('anim'));
    dispatch(toggleDialog('layerSettings'));
  };

  return (
    <div className="pp-cb-vbar">
      <div className="pp-cb-vgrp">
        <span className="pp-cb-vl">{t('show.show_vbar_label')}</span>
        <div className="pp-cb-seg">
          {VBAR_FITS.map((mode) => (
            <button key={mode} type="button" className={fit === mode ? 'on' : ''} onClick={() => setFitMode(mode)}>
              {t(`dragdrop.image_fit.${mode}`)}
            </button>
          ))}
        </div>
      </div>
      <button type="button" className="pp-cb-vchip" onClick={openTransitionTab}>
        <i className="bx bx-transfer-alt" />
        {t('show.show_vbar_transition', { label: transLabel })}
      </button>
      {/* F — "remove whole playlist from layer": calls onRemovePlaylist, which
          is CampaignVibesSection's removeMediaRow against this row's own
          campaign_playlist_media_id — same mutation the accordion header's
          own del button uses for shared/picked playlists. This is the ONLY
          way to detach a non-empty own-auto-playlist (its flat sub-items
          only offer per-item delete, no row-level one) since the bar now
          lives at section level, once, instead of per-row. */}
      <button type="button" className="pp-cb-chipbtn del" title={t('show.show_vibes_remove_playlist')} onClick={onRemovePlaylist}>
        <i className="bx bx-trash" />
      </button>
      <div className="pp-cb-vnote">{t('show.show_vbar_note')}</div>
    </div>
  );
}

export default CampaignVibesSection;
